import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Ship, Plane, Globe, Users, Calendar, Award, TrendingUp, MapPin } from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2500, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{prefix}{count}{suffix}</span>;
};

const Stats = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const content = {
    pt: {
      badge: 'A WELL CARGO EM NÚMEROS',
      title: 'Já movimentamos',
      titleHighlight: 'o mundo',
      subtitle: 'Nossa trajetória é marcada por números que refletem compromisso, experiência e resultados consistentes no comércio exterior.',
    },
    en: {
      badge: 'WELL CARGO IN NUMBERS',
      title: 'We have moved',
      titleHighlight: 'the world',
      subtitle: 'Our trajectory is marked by numbers that reflect commitment, experience and consistent results in foreign trade.',
    },
    es: {
      badge: 'WELL CARGO EN NÚMEROS',
      title: 'Ya movimos',
      titleHighlight: 'el mundo',
      subtitle: 'Nuestra trayectoria está marcada por números que reflejan compromiso, experiencia y resultados consistentes en el comercio exterior.',
    },
  };

  const mainStats = [
    {
      id: 1,
      value: 24,
      suffix: 'Mil',
      label: { pt: 'Contêineres pelo Mar', en: 'Containers by Sea', es: 'Contenedores por Mar' },
      description: { pt: 'Contêineres movimentados', en: 'Containers moved', es: 'Contenedores movidos' },
      icon: Ship,
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&q=80',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      id: 2,
      value: 24,
      suffix: 't',
      label: { pt: 'Toneladas pelo Ar', en: 'Tons by Air', es: 'Toneladas por Aire' },
      description: { pt: 'Cargas aéreas transportadas', en: 'Air cargo transported', es: 'Cargas aéreas transportadas' },
      icon: Plane,
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80',
      color: 'from-purple-500 to-pink-500',
    },
    {
      id: 3,
      value: 95,
      prefix: '+',
      suffix: '',
      label: { pt: 'Países Atendidos', en: 'Countries Served', es: 'Países Atendidos' },
      description: { pt: 'Presença global', en: 'Global presence', es: 'Presencia global' },
      icon: Globe,
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80',
      color: 'from-[#3ECDA3] to-emerald-400',
    },
  ];

  const secondaryStats = [
    {
      value: 15,
      suffix: '+',
      label: { pt: 'Anos de Mercado', en: 'Years in Market', es: 'Años en el Mercado' },
      icon: Calendar,
    },
    {
      value: 500,
      suffix: '+',
      label: { pt: 'Clientes Ativos', en: 'Active Clients', es: 'Clientes Activos' },
      icon: Users,
    },
    {
      value: 98,
      suffix: '%',
      label: { pt: 'Taxa de Satisfação', en: 'Satisfaction Rate', es: 'Tasa de Satisfacción' },
      icon: Award,
    },
    {
      value: 4,
      suffix: '',
      label: { pt: 'Escritórios no Brasil', en: 'Offices in Brazil', es: 'Oficinas en Brasil' },
      icon: MapPin,
    },
  ];

  const c = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden transition-colors duration-300">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3ECDA3]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#3ECDA3 1px, transparent 1px), linear-gradient(90deg, #3ECDA3 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block bg-[#3ECDA3]/10 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6">
            {c.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-4">
            {c.title}{' '}
            <span className="text-[#3ECDA3] italic">{c.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            {c.subtitle}
          </p>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {mainStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={stat.id}
                className={`group relative overflow-hidden rounded-3xl transition-all duration-700 hover:-translate-y-2 hover:shadow-2xl ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={stat.image}
                    alt={stat.label[language]}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${stat.color} opacity-80 mix-blend-multiply`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                </div>

                {/* Content */}
                <div className="relative p-8 md:p-10 h-[280px] md:h-[320px] flex flex-col justify-between">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>

                  {/* Stats */}
                  <div>
                    <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                      <AnimatedCounter 
                        end={stat.value} 
                        suffix={stat.suffix} 
                        prefix={stat.prefix || ''} 
                        isVisible={isVisible} 
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-1">
                      {stat.label[language]}
                    </h3>
                    <p className="text-white/70 text-sm">
                      {stat.description[language]}
                    </p>
                  </div>

                  {/* Decorative Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Secondary Stats */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {secondaryStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="group bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 hover:border-[#3ECDA3]/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-[#3ECDA3]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#3ECDA3]/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-[#3ECDA3]" />
                  </div>

                  {/* Content */}
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                      <AnimatedCounter 
                        end={stat.value} 
                        suffix={stat.suffix} 
                        isVisible={isVisible} 
                      />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {stat.label[language]}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Stats;
